import { CartIcon, EditIcon, EllipsisIcon, PhotosIcon, ShoppingBagIcon, StackIcon, TrashIcon } from "@xola/icons";
import { Badge, flash, PopoverList, Tooltip } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { ReactElement, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { ButtonType, EmailTemplate, Membership, Permission } from "types";
import { api } from "@/api/api";
import { Currency } from "@/components/Common/Currency";
import { IsGranted } from "@/components/Common/IsGranted";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ProductImage } from "@/components/Common/ProductImage";
import { PromptModal } from "@/components/Common/PromptModal";
import { PreviewMessageTemplate } from "@/components/Customers/Templates/PreviewMessageTemplate";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";
import { getButtonUrl, isX2CheckoutSupported, openCheckoutIframe } from "@/models/ButtonCode";
import { isXolaAdmin } from "@/models/User";

export const MembershipTile = ({ membership }: { membership: Membership }) => {
    const history = useHistory();
    const { isGranted } = useAuthorization();

    const excerptRef = useRef<HTMLDivElement | null>(null);
    const element = excerptRef.current?.childNodes?.[0] as HTMLElement | null;
    const isTruncated = element ? element.scrollHeight > element.clientHeight : false;

    const handleClick = () => {
        if (!isGranted(Permission.MEMBERSHIPS_MODIFY)) {
            return;
        }

        history.push(`/products/memberships/${membership.id}/description`);
    };

    return (
        <div
            className="group flex cursor-pointer items-center rounded p-2"
            data-testid="membership-tile"
            onClick={handleClick}
        >
            <ProductImage product={membership} size="huge" />
            <div className="w-full">
                <div className="min-h-8 flex justify-between">
                    <NoTranslate className="text-lg font-bold line-clamp-2">{membership.name}</NoTranslate>

                    <div className="flex min-w-24 justify-end space-x-2">
                        <IsGranted permissions={Permission.MEMBERSHIPS_MODIFY}>
                            <SecondaryButton
                                size="small"
                                className="invisible group-hover:visible"
                                icon={<EditIcon />}
                                data-testid="membership-tile-edit-button"
                            >
                                Edit
                            </SecondaryButton>
                        </IsGranted>

                        <IsGranted permissions={Permission.MEMBERSHIPS_MODIFY}>
                            <MembershipActionButton membership={membership} />
                        </IsGranted>
                    </div>
                </div>

                <Tooltip
                    disabled={!isTruncated}
                    content={<NoTranslate data-testid="membership-tile-tooltip-desc">{membership.desc}</NoTranslate>}
                >
                    <div ref={excerptRef}>
                        <NoTranslate className="pt-1 text-base line-clamp-2" data-testid="experience-tile-desc">
                            {membership.desc}
                        </NoTranslate>
                    </div>
                </Tooltip>

                <div className="mt-2 flex space-x-2">
                    <ProductBadge text={<Currency>{membership.price}</Currency>} size="medium" icon={<StackIcon />} />
                </div>
            </div>
        </div>
    );
};

interface ProductBadgeProps {
    color?: string;
    text: string | ReactElement;
    icon: ReactElement;
    size: string;
}

const ProductBadge = ({ color = "secondary", text, icon, size }: ProductBadgeProps) => {
    return (
        <Badge color={color} size={size} icon={icon}>
            {text}
        </Badge>
    );
};

const MembershipActionButton = ({ membership }: { membership: Membership }) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const invalidate = useInvalidate();
    const [isEmailPreviewVisible, { toggle: toggleEmailPreview }] = useVisibility(false);
    const history = useHistory();
    const seller = useSeller();
    const user = useUser();

    const show = (e: any) => {
        e.stopPropagation();
        setVisible(true);
    };

    const hide = (e: any) => {
        if ("hide" in e) {
            e.hide();
        } else {
            e.stopPropagation();
        }

        setVisible(false);
    };

    const { mutateAsync: deleteMembership, isLoading: isDeleting } = useMutation(async (id: ID) => {
        return api.delete(`/memberships/${id}`);
    });

    const handleToggleDeleteModal = async (e: any) => {
        e.stopPropagation();
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const invalidateProduct = (id: ID) => {
        invalidate("products", id);
        invalidate("memberships", id);
        invalidate("giftOffers");
    };

    const handleDeleteConfirm = async () => {
        if (membership?.id) {
            await deleteMembership(membership.id);
            invalidateProduct(membership.id);
            flash.success("Membership deleted");
        }

        setIsDeleteModalOpen(false);
    };

    // uncomment when these functionalities are ready
    /*
    const handlePreviewEmail = (e: any) => {
        e.stopPropagation();
        toggleEmailPreview();
        setVisible(false);
    };
    */

    const handleCheckout = (e: any) => {
        e.stopPropagation();
        const url = getButtonUrl(seller, "", ButtonType.SINGLE, membership);
        if (url) {
            window.open(url, "_blank");
        }

        setVisible(false);
    };

    const handleX2Checkout = (e: any) => {
        e.stopPropagation();
        const url = getButtonUrl(seller, "", ButtonType.SINGLE + "_x2", membership);
        setVisible(false);
        openCheckoutIframe(url);
    };

    const handleEdit = (e: any) => {
        e.stopPropagation();
        const section = e.target.getAttribute("name") === "Edit" ? "descriptive" : "photos";
        history.push(`/products/memberships/${membership.id}/${section}`);
    };

    const showX2Checkout = isX2CheckoutSupported({ type: ButtonType.SINGLE }, membership);

    return (
        <>
            <PopoverList visible={visible} className="w-50 items-center" zIndex="10" onClickOutside={hide}>
                <SecondaryButton
                    size="small"
                    data-testid="experience-action-button-ellipsis"
                    onClick={visible ? hide : show}
                >
                    <EllipsisIcon />
                </SecondaryButton>

                <PopoverList.Item name="Photos" role="button" onClickItem={handleEdit}>
                    <PhotosIcon />
                    <span>Photos</span>
                </PopoverList.Item>

                {(isXolaAdmin(user) || !showX2Checkout) && (
                    <PopoverList.Item name="Preview Checkout" role="button" onClickItem={handleCheckout}>
                        <ShoppingBagIcon className="top-0" />
                        <span>Preview Checkout</span>
                    </PopoverList.Item>
                )}

                {showX2Checkout && (
                    <PopoverList.Item name="Preview X2 Checkout" role="button" onClickItem={handleX2Checkout}>
                        <CartIcon className="top-0" />
                        <span>Preview {isXolaAdmin(user) ? "X2 " : ""}Checkout</span>
                    </PopoverList.Item>
                )}

                {/* <PopoverList.Item name="Preview Email" onClickItem={handlePreviewEmail}>
                    <EmailIcon />
                    <span>Preview Email</span>
                </PopoverList.Item> */}
                <PopoverList.Item name="Delete" role="button" onClickItem={handleToggleDeleteModal}>
                    <TrashIcon />
                    <span>Delete</span>
                </PopoverList.Item>
            </PopoverList>
            <PreviewMessageTemplate
                isOpen={isEmailPreviewVisible}
                payload={{
                    template: EmailTemplate.PURCHASE_CONFIRMATION,
                    experience: membership.id,
                }}
                onClose={() => toggleEmailPreview()}
            />
            <PromptModal
                title="Delete Membership?"
                isLoading={isDeleting}
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onClose={handleToggleDeleteModal}
            >
                Are you sure you want to delete this membership?
            </PromptModal>
        </>
    );
};
